// Theatre (rebuild.md §4.9, task 2.12): rewatch any unlocked excerpt, read its transcript, and "Play the
// whole film" (the 21 excerpts in order) once the Epilogue is filed (or in Classroom mode).
import { useEffect, useState } from 'preact/hooks';
import { isFiled, markWatched, save } from '@app/state.ts';
import { chapter } from '@content/chapters.ts';
import { CutscenePlayer } from '@media/CutscenePlayer.tsx';
import { loadMediaManifest, mediaManifest, mediaUrl, type MediaExcerpt } from '@media/manifest.ts';
import { parseVtt, type Cue } from '@media/vtt.ts';
import { Button, Modal } from '@ui/core.tsx';
import { Screen } from './Shell.tsx';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

export function excerptUnlocked(e: MediaExcerpt): boolean {
  return save.value.classroom || isFiled(e.stage);
}

export function Theatre() {
  const [playing, setPlaying] = useState<{ list: MediaExcerpt[]; i: number } | null>(null);
  const [transcript, setTranscript] = useState<{ title: string; cues: Cue[] } | null>(null);
  useEffect(() => void loadMediaManifest(), []);
  const all = mediaManifest.value.excerpts;
  const unlocked = all.filter(excerptUnlocked);
  const wholeFilm = save.value.classroom || isFiled('epilogue');

  if (playing) {
    const e = playing.list[playing.i]!;
    const c = chapter(e.stage);
    const next = () => (playing.i + 1 < playing.list.length ? setPlaying({ ...playing, i: playing.i + 1 }) : setPlaying(null));
    return (
      <CutscenePlayer
        key={e.id}
        id={e.id}
        role={e.role}
        firstViewing={false}
        autoStart={playing.i > 0}
        title={`${c?.label ?? ''} · ${c?.title ?? ''}${e.role === 'post' ? ' (ending)' : ''}`}
        onEnded={() => {
          markWatched(e.id);
          next();
        }}
        onSkip={next}
      />
    );
  }

  const openTranscript = async (e: MediaExcerpt) => {
    const url = mediaUrl(e.vtt);
    const text = url ? await fetch(url).then((r) => (r.ok ? r.text() : '')).catch(() => '') : '';
    const c = chapter(e.stage);
    setTranscript({ title: `${c?.label ?? e.id} transcript`, cues: parseVtt(text) });
  };

  return (
    <Screen
      title="Theatre"
      actions={
        wholeFilm && (
          <Button tone="primary" onClick={() => setPlaying({ list: all, i: 0 })}>
            ▶ Play the whole film
          </Button>
        )
      }
    >
      {!wholeFilm && <p class="dim">Finish a chapter to add its scenes here. The whole film unlocks after the Epilogue.</p>}
      {unlocked.length === 0 ? (
        <p class="dim">Nothing to rewatch yet. Play the Prologue first.</p>
      ) : (
        // role="list" restores list semantics that Safari drops for list-style: none
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ul class="theatre-list" role="list">
          {unlocked.map((e) => {
            const c = chapter(e.stage);
            const title = `${c?.label ?? e.id}${e.role === 'post' ? ' · ending' : ''}`;
            return (
              <li key={e.id} class="theatre-item">
                <button type="button" class="thumb" onClick={() => setPlaying({ list: [e], i: 0 })} aria-label={`Play ${title}`}>
                  {e.thumb && <img src={mediaUrl(e.thumb)!} alt="" loading="lazy" />}
                  <span class="play-badge" aria-hidden="true">
                    ▶
                  </span>
                </button>
                <div class="theatre-meta">
                  <h3>{title}</h3>
                  <span class="dim">
                    {c?.title} · {fmt(e.seconds)}
                  </span>
                  <Button tone="ghost" onClick={() => void openTranscript(e)}>
                    Transcript
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Modal open={!!transcript} onClose={() => setTranscript(null)} title={transcript?.title ?? ''}>
        <div class="transcript">
          {transcript?.cues.length ? (
            transcript.cues.map((c) => (
              <p key={c.id}>
                <span class="who">{c.speaker ?? '—'}</span>
                {c.text}
              </p>
            ))
          ) : (
            <p class="dim">The transcript isn’t available offline.</p>
          )}
        </div>
      </Modal>
    </Screen>
  );
}
